/**
 * The product's module taxonomy (Tier 5 / US-24).
 *
 * WHY THIS EXISTS. Raw page views are already captured, and the destination
 * can group them by URL — but URL grouping breaks the day a route is renamed,
 * and it silently splits one module's history into a before and an after that
 * nobody notices until a chart goes flat. It also cannot express module
 * IDENTITY: `/schedule-of-rates` and `/schedule-of-rates-v1` are two routes
 * and one product area, and no amount of regex in Mixpanel makes that
 * decision correctly on our behalf.
 *
 * THE DURABILITY MECHANISM is the split below: {@link PayModule} identifiers
 * are the stable thing and must not be renamed, while
 * {@link MODULE_ROUTE_SEGMENTS} is expected to change. Rename a route and you
 * add its new segment here pointing at the SAME module id; the reporting
 * history stays continuous across the rename, which is the whole point and
 * the thing URL grouping cannot do.
 *
 * Adding a module: add the id to the union and one entry to the map. Keep
 * them camelCase, matching the event-name convention in `./events`.
 */
export type PayModule =
  | 'activities'
  | 'allowances'
  | 'apiKeys'
  | 'assets'
  | 'billableUnits'
  | 'billing'
  | 'competencies'
  | 'compliance'
  | 'connect'
  | 'costCodes'
  | 'dockets'
  | 'documents'
  | 'evidenceExports'
  | 'forms'
  | 'hseq'
  | 'importStudio'
  | 'integrations'
  | 'invoices'
  | 'maintenance'
  | 'marketplace'
  | 'materials'
  | 'members'
  | 'messaging'
  | 'milo'
  | 'myCompetencies'
  | 'myShifts'
  | 'orders'
  | 'payRates'
  | 'projects'
  | 'purchaseOrders'
  | 'quotes'
  | 'reports'
  | 'roles'
  | 'scheduleOfRates'
  | 'scheduleOfValues'
  | 'scheduling'
  | 'settings'
  | 'siteDiaries'
  | 'sms'
  | 'subcontractors'
  | 'supplierInvoices'
  | 'suppliers'
  | 'tags'
  | 'timesheets'
  | 'usage'
  | 'variationDockets'
  | 'workpacks';

/**
 * Route segment -> module. SEVERAL SEGMENTS MAY SHARE ONE MODULE, and that is
 * the feature, not an accident: it is how a rename, a v2 rebuild or a split
 * route keeps reporting against one continuous history.
 *
 * `cost-code-categories` and `cost-codes` are two routes over one product
 * area. `schedule-of-rates-v1` is the legacy surface of `schedule-of-rates`
 * and is deliberately the SAME module — the question "is anyone using
 * Schedule of Rates" should not be answered twice. `paper-dockets` is the
 * photographed-docket queue for the same Dockets area.
 */
const MODULE_ROUTE_SEGMENTS: Readonly<Record<string, PayModule>> = {
  activities: 'activities',
  allowances: 'allowances',
  'api-keys': 'apiKeys',
  assets: 'assets',
  'billable-units': 'billableUnits',
  billing: 'billing',
  competencies: 'competencies',
  compliance: 'compliance',
  connect: 'connect',
  'cost-code-categories': 'costCodes',
  'cost-codes': 'costCodes',
  dockets: 'dockets',
  documents: 'documents',
  'evidence-exports': 'evidenceExports',
  // PAY-3666: the nav's Job Costing entry, a redirect into the /orders tabs.
  'job-costing': 'orders',
  forms: 'forms',
  hseq: 'hseq',
  // PAY-5171: the visual import canvas.
  'import-studio': 'importStudio',
  integrations: 'integrations',
  invoices: 'invoices',
  // Asset Maintenance: requests, work orders and their labour/parts costs.
  maintenance: 'maintenance',
  marketplace: 'marketplace',
  materials: 'materials',
  members: 'members',
  users: 'members',
  messaging: 'messaging',
  milo: 'milo',
  'my-competencies': 'myCompetencies',
  'my-shifts': 'myShifts',
  orders: 'orders',
  'paper-dockets': 'dockets',
  'pay-rates': 'payRates',
  projects: 'projects',
  'purchase-orders': 'purchaseOrders',
  quotes: 'quotes',
  reports: 'reports',
  roles: 'roles',
  'schedule-of-rates': 'scheduleOfRates',
  'schedule-of-rates-v1': 'scheduleOfRates',
  'schedule-of-values': 'scheduleOfValues',
  scheduling: 'scheduling',
  settings: 'settings',
  'site-diaries': 'siteDiaries',
  sms: 'sms',
  subcontractors: 'subcontractors',
  'supplier-invoices': 'supplierInvoices',
  // PAY-4282: the supplier portal's time submission and Time list, in the
  // supplier nav's Procurement group beside Purchase Orders. A supplier's
  // time is booked to (and consumes) its purchase order lines.
  'supplier-time': 'purchaseOrders',
  suppliers: 'suppliers',
  tags: 'tags',
  timesheets: 'timesheets',
  usage: 'usage',
  // PAY-4220: the variation docket book (register, detail, export).
  variations: 'variationDockets',
  workpacks: 'workpacks',
};

/**
 * The segments that exist under `home/(user)`, the personal-account shell.
 *
 * Deliberately a SHORT allowlist rather than the whole taxonomy: the
 * two-segment path `/home/<x>` is ambiguous between a personal module and a
 * team account's home page, and every module name is a legal tenant slug.
 * Only these can be the former.
 */
const PERSONAL_ROUTE_SEGMENTS = new Set(['billing', 'settings']);

/**
 * The module a path belongs to, or `undefined` for anything outside the
 * authenticated product — marketing, auth, admin, the public portals.
 *
 * `undefined` rather than an 'other' bucket on purpose: an 'other' module
 * would accumulate every un-mapped route and read as a real, growing product
 * area on a chart. Something unmapped should be absent, and absent is
 * noticeable.
 *
 * Accepts a `/[locale]`-prefixed path, which is what `usePathname` returns
 * here, and tolerates its absence.
 */
export function resolvePayModule(pathname: string): PayModule | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const homeIndex = segments.indexOf('home');

  if (homeIndex === -1) return undefined;

  const teamModule = segments[homeIndex + 2];
  const personalModule = segments[homeIndex + 1];

  // THE TEAM SHAPE WINS OUTRIGHT when there is a third segment — it is never
  // a fallback.
  //
  // `/home/<slug>/<module>` and `/home/<module>` are ambiguous only in the
  // slug position, and a tenant may legitimately be called `projects`,
  // `reports` or `invoices`. Falling back to that position whenever the
  // module segment was missing or unmapped attributed such an account's HOME
  // page — and every unmapped route under it — to the module sharing its
  // name. Better to report nothing than to report a module nobody opened.
  if (teamModule !== undefined) {
    return teamModule in MODULE_ROUTE_SEGMENTS
      ? MODULE_ROUTE_SEGMENTS[teamModule]
      : undefined;
  }

  // No third segment. That is EITHER the personal-account shape
  // (`/home/billing`) or a team account's home page (`/home/<slug>`), and
  // the segment alone cannot tell them apart — so only the handful of
  // routes that genuinely exist under `home/(user)` are accepted. A tenant
  // called `projects` lands on its own home page, not in the projects
  // module.
  if (personalModule && PERSONAL_ROUTE_SEGMENTS.has(personalModule)) {
    return MODULE_ROUTE_SEGMENTS[personalModule];
  }

  return undefined;
}
